import React from "react";
import { Category } from "../../../types/form";
import type { FeedbackItemDetail } from "../../../API/feedbackAPI";
import { evaluateVisibility } from "../../../utils/formVisibility";
import QuestionRenderer from "./QuestionRenderer";

interface QuestionsListProps {
  activeCategory: Category | null;
  formData: Record<string, any>;
  /** Form-wide visible question ids, transitive (client worklist #84). */
  visibleIds?: Set<number>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleMultipleEntryChange: (
    inputName: string,
    idx: number,
    value: any
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  handleCompositeFieldChange: (
    inputName: string,
    compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any
  ) => void;
  handleMultipleCompositeChange: (
    inputName: string,
    entryIdx: number,
    fieldKey: string,
    value: any
  ) => void;
  isLoading: boolean;
  loadError: string | null;
  validationErrors?: Record<string, string>;
  onOpenHintsWithHint?: (hint: { title?: string; content: string } | null) => void;
  questionIdsWithFeedback?: Set<number>;
  onOpenFeedbackWithQuestion?: (questionId: number) => void;
  restrictEditsToFeedbackFields?: boolean;
  questionIdsWithPendingFeedback?: Set<number>;
  isFormViewOnly?: boolean;
  activeFeedbackItem?: FeedbackItemDetail | null;
  applicationId?: number;
}

const QuestionsList: React.FC<QuestionsListProps> = ({
  activeCategory,
  formData,
  visibleIds,
  setFormData,
  handleChange,
  handleMultipleEntryChange,
  handleAddEntry,
  handleRemoveEntry,
  handleCompositeFieldChange,
  handleMultipleCompositeChange,
  isLoading,
  loadError,
  validationErrors,
  onOpenHintsWithHint,
  questionIdsWithFeedback,
  onOpenFeedbackWithQuestion,
  restrictEditsToFeedbackFields = false,
  questionIdsWithPendingFeedback,
  isFormViewOnly = false,
  activeFeedbackItem,
  applicationId,
}) => {
  if (isLoading) {
    return <p className="text-[#1B365D]">Loading questions...</p>;
  }

  if (loadError) {
    return <p className="text-red-600">{loadError}</p>;
  }

  if (!activeCategory) {
    return <p className="text-[#1B365D]">No category selected.</p>;
  }

  const questions = activeCategory.questions || [];

  // Sort questions by order
  const sortedQuestions = [...questions].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  // Filter by Visibility Rules
  const visibleQuestions = sortedQuestions.filter((q) =>
    evaluateVisibility(q, formData, visibleIds)
  );

  if (visibleQuestions.length === 0) {
    return (
      <p className="text-[#1B365D]">No visible questions in this section.</p>
    );
  }

  return (
    <div className="space-y-8">
      {visibleQuestions.map((question) => (
        <QuestionRenderer
          key={question.id}
          question={question}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleMultipleEntryChange={handleMultipleEntryChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          handleCompositeFieldChange={handleCompositeFieldChange}
          handleMultipleCompositeChange={handleMultipleCompositeChange}
          validationErrors={validationErrors}
          onOpenHintsWithHint={onOpenHintsWithHint}
          hasFeedback={questionIdsWithFeedback?.has(question.id)}
          onOpenFeedbackWithQuestion={onOpenFeedbackWithQuestion}
          readOnly={
            isFormViewOnly ||
            (!!restrictEditsToFeedbackFields &&
              !questionIdsWithPendingFeedback?.has(question.id))
          }
          activeFeedbackItem={activeFeedbackItem}
          applicationId={applicationId}
        />
      ))}
    </div>
  );
};

export default QuestionsList;
